<script setup lang="ts">
import EventCheckpointAutocomplete from '@/components/Filters/EventCheckpointAutocomplete.vue'
import type {
    DeviceFiltersType,
    LocalStorageFilter,
    QueryDetectionsFilter
} from '@/types/filters/DeviceFilters'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import { StackLayout } from '@rfc/rfc-components-library'
import { ELocalStorage, type ExtendedPrimitives } from '@rfc/rfc-components-library/types'
import { getItemFromStorage } from '@rfc/rfc-components-library/utils'
import { ref, watch } from 'vue'

type Props = {
    filters?: DeviceFiltersType
    hideEventSelector?: boolean
    deviceId?: number
    data?: DeviceModel
    query?: QueryDetectionsFilter

    event?: EventModel
    checkpoint?: CheckpointModel
    setDeviceOptions?: (value?: AssociatedDeviceModel[] | undefined) => void
    change: (value?: Record<string, ExtendedPrimitives>) => void
}
const props = defineProps<Props>()
type Emits = {
    (e: 'change', value?: Record<string, ExtendedPrimitives>): void
    (e: 'setDeviceOptions', value?: AssociatedDeviceModel[] | undefined): void
}
const emit = defineEmits<Emits>()

const selectedEvent = ref<Partial<EventModel> | undefined>(
    props.event ??
        props.query?.event ??
        props.data?.event ??
        getItemFromStorage<LocalStorageFilter>(ELocalStorage.FILTERS)?.event
)
const selectedCheckpoint = ref<Partial<CheckpointModel> | undefined>(
    props.checkpoint ??
        props.query?.checkpoint ??
        props.data?.checkpoint ??
        getItemFromStorage<LocalStorageFilter>(ELocalStorage.FILTERS)?.checkpoint
)

watch(
    () => props.event,
    newValue => {
        selectedEvent.value = newValue
        props.change?.({ event_id: newValue?.id, checkpoint_id: undefined, device_id: undefined })
        props.setDeviceOptions?.([])
    },
    { immediate: true }
)

const handleSelectEvent = (event?: Partial<EventModel>) => {
    selectedEvent.value = event
    props.change?.({ checkpoint_id: undefined, event_id: event?.id, device_id: undefined })
    props.setDeviceOptions?.([])
}

const handleSelectCheckpoint = (checkpoint?: CheckpointModel) => {
    selectedCheckpoint.value = checkpoint
    props.change?.({ checkpoint_id: checkpoint?.id, device_id: undefined })
    props.setDeviceOptions?.(checkpoint?.associates)
}

defineExpose({ event: selectedEvent, checkpoint: selectedCheckpoint })
</script>

<template>
    <StackLayout :gap="8" isResponsive>
        <EventCheckpointAutocomplete
            :event="selectedEvent"
            :checkpoint="selectedCheckpoint"
            filterAllEvents
            :hideEventSelector="hideEventSelector"
            @selectEvent="handleSelectEvent"
            @selectCheckpoint="handleSelectCheckpoint" />
    </StackLayout>
</template>
